import { ListBody, listSpec } from '@bemor/web-kit';
import { Button, DateTime, Input, PageHeader } from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import type { SearchHit } from '@/api/types';
import { useList } from '@/lib/queries';
import { subjectPath } from '@/pages/queues/index';

/** The reads of `/search` from its URL (BM-46 §2.0): none until the query has three characters. */
export const searchReads = (search: URLSearchParams) => {
    const q = search.get('q') ?? '';
    return { q, list: q.length >= 3 ? listSpec('/staff/search', { q }) : null };
};

export function SearchPage() {
    const [params, setParams] = useSearchParams();
    const q = params.get('q') ?? '';
    const [input, setInput] = useState(q);
    const query = useList<SearchHit>(
        '/staff/search',
        searchReads(params).list?.params ?? { q },
        q.length >= 3,
    );
    return (
        <div>
            <PageHeader
                title="Search"
                meta="A public id, an email (matched by hash, never stored in clear), a card's last four digits or a name."
            />
            <form
                method="post"
                className="mb-4 flex gap-2"
                role="search"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (input.trim().length >= 3)
                        setParams({ q: input.trim() });
                }}
            >
                <Input
                    aria-label="Search"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Search by id, last four or name"
                    autoFocus
                />
                <Button type="submit">Search</Button>
            </form>
            {q.length >= 3 ? (
                <ListBody
                    query={query}
                    columns={[
                        {
                            key: 'display',
                            label: 'Result',
                            locked: 'start',
                            cell: (h) => {
                                const to =
                                    subjectPath(h.entity_type, h.id) ??
                                    `/audit?entity_id=${h.id}`;
                                return (
                                    <Link to={to} className="hover:underline">
                                        {h.display}
                                    </Link>
                                );
                            },
                        },
                        {
                            key: 'type',
                            label: 'Type',
                            cell: (h) => h.entity_type,
                        },
                        {
                            key: 'id',
                            label: 'Id',
                            cell: (h) => (
                                <span className="font-mono text-xs">
                                    {h.id}
                                </span>
                            ),
                        },
                        {
                            key: 'merchant',
                            label: 'Merchant',
                            cell: (h) => (
                                <span className="font-mono text-xs">
                                    {h.merchant_id ?? '—'}
                                </span>
                            ),
                        },
                        {
                            key: 'when',
                            label: 'Seen',
                            kind: 'datetime',
                            cell: (h) => (
                                <DateTime value={h.occurred_at} empty="—" />
                            ),
                            noTruncate: true,
                        },
                    ]}
                    rowKey={(h) => `${h.entity_type}:${h.id}`}
                    empty={
                        <p className="text-muted-foreground p-6 text-center text-sm">
                            No match for “{q}”.
                        </p>
                    }
                />
            ) : (
                <p className="text-muted-foreground text-sm">
                    Type at least three characters.
                </p>
            )}
        </div>
    );
}
